export enum ColorName {
  Black = "black",
  CharcoalGray = "charcoal-gray",
  Brown = "brown",
}

export enum ComponentId {
  TACTSUIT_TECH_SPECS = "TACTSUIT_TECH_SPECS",
  OVERVIEW = "OVERVIEW",
  GNB = "Gnb",
  BUY_TACTSUIT_GAMEKEY_OPTION = "buy-tactsuit-gamekey-{index}",
  BUY_TACTSUIT_COLOR_OPTION = "buy-tactsuit-color",
  BUY_TACTSUIT_SIZE_OPTION = "buy-tactsuit-size",
}

// buy-tactsuit 옵션 섹션의 필수-미선택 에러 wrapper.
// 모바일: 좌우 풀블리드(-mx-4 + px-4 재패딩) + 라운드 없음. 데스크탑: rounded 카드.
// bg-red-10 은 에러 시점에만 호출처에서 토글. 디자인 스펙 단일 출처.
// 주의: 모바일 풀블리드는 호출처(부모)가 px-4(16px) 가로 패딩을 준다는 전제. -mx-4 가
//   그 px-4 를 정확히 상쇄해 화면 끝에 닿는다. 부모 패딩을 바꾸면 이 -mx-4 도 같이 맞출 것
//   (안 그러면 모바일 가로 overflow / 풀블리드 어긋남). py-4 는 에러 배경 reflow 방지용 상시 예약.
// 데스크탑 가로 패딩은 **0** — 피그마(28715:139529)에서 옵션 헤더·스와치가 제품명·버튼과
//   같은 x=0 선에 정렬된다. p-4 로 두면 옵션 콘텐츠만 16px 안쪽으로 밀린다.
//   세로(py-4)는 남긴다 — 에러 배경이 켜질 때 reflow 를 막는 예약 공간이라 지우면 화면이 튄다.
export const ERROR_SECTION_WRAPPER =
  "-mx-4 rounded-none px-4 py-4 laptop:mx-0 laptop:rounded-xl laptop:px-0 laptop:py-4";

// 구매 레일(리뉴얼) 전용 에러 강조 — **레이아웃에 영향을 주지 않는다.**
//
// ERROR_SECTION_WRAPPER 는 py-4 로 자리를 상시 예약해 reflow 를 막는데, 새 레일은
// 섹션 간격이 피그마 수치(48px/20px/12px)로 고정돼 있어 그 16px 이 전부 어긋난다
// (실측: 박스 간 48px 인데 텍스트 기준으론 64px 로 보임).
// → pseudo-element 로 -inset-4 배경만 깔아 **reflow 0 + 간격 정확** 둘 다 얻는다.
// `isolate` 필수 — 없으면 `-z-10` 이 조상 배경 뒤로 빠져 핑크가 안 보인다.
// 가로 범위: 모바일은 풀블리드(-inset-x-4). 데스크탑도 **레일의 좌우 패딩 16px 까지**
// 확장한다(-inset-x-4 그대로) — 배경이 글자에 딱 붙지 않게 하려고 레일에 px-4 를 둔
// 것이라, 여기서 그만큼 채워야 여백이 생긴다. 레일 박스(496)를 넘지 않으므로 좌측
// 이미지 열을 침범하지 않는다.
export const RAIL_ERROR_HIGHLIGHT =
  "relative isolate before:absolute before:-inset-4 before:-z-10 before:rounded-none before:bg-red-10 laptop:before:rounded-xl";

export const ForeignExchangeRate = {
  DollarToYen: 140,
};

/**
 * PC Player 설치본이 올라가는 **릴리스 애플리케이션 이름**. sdk-apis 가 이 이름으로
 * 최신본을 찾아 서명 URL 로 리다이렉트한다.
 *
 * 🔴 **`bHapticsPlayer` 릴리스 앱을 여기 넣지 말 것.** 그 앱은 구 PC Player 2.x 의 인앱
 * 업데이트 조회가 보는 것이고, 신 PCP 로 넘기는 마이그레이션 패키지가 올라간다
 * (bhaptics-etc/bhaptics-player-win#2790). 홈페이지가 그걸 가리키면 방문자가 단계배포 상태에
 * 따라 다른 것을 받는다. 구 PCP 2.x 다운로드 자체는 deprecated 로 제거됐다(#5766).
 */
export const PcPlayerReleaseApp = {
  win: "bhaptics-player-win-beta",
  mac: "bhaptics-player-mac-beta",
} as const;

/** 릴리스 애플리케이션 이름으로 최신 설치본을 받는 다운로드 URL. */
export const pcPlayerDownloadUrl = (
  app: (typeof PcPlayerReleaseApp)[keyof typeof PcPlayerReleaseApp],
) =>
  `https://sdk-apis.bhaptics.com/api/v1/release-files/download-latest-by-application-name?name=${app}`;

export const Links = {
  VRPVivePort:
    "https://www.viveport.com/apps/3b103f95-d19c-4859-a2a5-8f67d936d234",
  VRPPicoStore:
    "https://store-global.picoxr.com/global/detail/1/7569072425799991301",
  DesignerTutorial: "https://www.youtube.com/watch?v=7H62Rksl9rQ",
  BhapticsDesigner: "https://designer.bhaptics.com/",
  BhapticsStudio: "https://studio.bhaptics.com/",
  BhapticsMetaQuest:
    "https://www.oculus.com/experiences/quest/3454125161357542/",
  /** 기본 PC Player 다운로드 = **신 PCP(3.x) Windows**. */
  BhapticsPcPlayer: pcPlayerDownloadUrl(PcPlayerReleaseApp.win),
  BhapticsIOS: "https://apps.apple.com/gb/app/bhaptics-player/id1367036609",
  BhapticsAndroid:
    "https://play.google.com/store/apps/details?id=com.bhaptics.player&hl=en",
};

export const KeyEventCode = {
  ArrowDown: 40,
  ArrowUp: 38,
  Enter: 13,
};
